import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { apiPost, apiDelete } from "@/lib/api";
import { toast } from "sonner";
import { Share2, Copy, X, Twitter, Loader2, ArrowRight } from "lucide-react";

/**
 * Share-to-social banner. Generates a server-side card at /api/share/card/:code.png
 * and a public landing page at /won/:code. NEVER includes PII — only a sanitized
 * headline + recovered total the user explicitly confirms.
 */
export default function ShareBanner({ total }) {
    const { refreshProfile, profile } = useApp();
    const [open, setOpen] = useState(false);
    const [headline, setHeadline] = useState(
        total && total > 0 ? `I found ${formatTotal(total)} I was owed on Owed.` : "I use Owed to claim grants I qualify for."
    );
    const [share, setShare] = useState(() => (
        profile?.share_code
            ? { code: profile.share_code, total: profile.share_total, headline: profile.share_headline }
            : null
    ));
    const [busy, setBusy] = useState(false);

    const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const imgUrl = share?.code ? `${backendBase}/api/share/card/${share.code}.png` : null;
    const pageUrl = share?.code ? `${origin}/won/${share.code}` : null;

    const generate = async () => {
        setBusy(true);
        try {
            const r = await apiPost("/share/generate", { headline });
            setShare(r);
            await refreshProfile();
            toast.success("Your share card is ready");
        } catch {
            toast.error("Couldn't generate share card");
        } finally { setBusy(false); }
    };

    const revoke = async () => {
        setBusy(true);
        try {
            await apiDelete("/share/mine");
            setShare(null);
            await refreshProfile();
            toast.success("Share link revoked");
        } catch {
            toast.error("Couldn't revoke");
        } finally { setBusy(false); }
    };

    const copyLink = async () => {
        if (!pageUrl) return;
        try {
            await navigator.clipboard.writeText(pageUrl);
            toast.success("Link copied");
        } catch { toast.error("Copy failed — select the URL manually"); }
    };

    const tweetUrl = share && headline
        ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(share.headline || headline)}&url=${encodeURIComponent(pageUrl || "")}`
        : null;

    return (
        <>
            <Card
                className="mt-10 p-6 sm:p-7 border-border bg-gradient-warm relative overflow-hidden"
                data-testid="share-banner"
            >
                <div aria-hidden className="absolute -top-6 -right-8 h-32 w-32 rounded-full bg-accent/20 blur-2xl" />
                <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <span className="h-11 w-11 rounded-xl bg-foreground text-background inline-flex items-center justify-center shrink-0">
                        <Share2 className="h-5 w-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">Flywheel</p>
                        <h3 className="mt-1 font-serif text-xl sm:text-2xl leading-tight">
                            Share what you found — help a friend find theirs.
                        </h3>
                        <p className="mt-1.5 text-sm text-muted-foreground max-w-xl leading-relaxed">
                            We'll generate a private, shareable card (no names, no programs — just the number).
                        </p>
                    </div>
                    <Button
                        onClick={() => setOpen(true)}
                        className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-5 shrink-0"
                        data-testid="share-banner-open-btn"
                    >
                        {share?.code ? "Manage share card" : "Create share card"} <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                </div>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl" data-testid="share-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Share your Owed card</DialogTitle>
                        <DialogDescription>
                            Only the headline + your total is public. Never your name, email, or which programs you applied to.
                        </DialogDescription>
                    </DialogHeader>

                    {!share?.code ? (
                        <div className="mt-4 space-y-4">
                            <div>
                                <Label className="text-xs">Headline</Label>
                                <Input
                                    value={headline}
                                    onChange={(e) => setHeadline(e.target.value)}
                                    maxLength={140}
                                    className="mt-1.5 h-11 rounded-xl"
                                    data-testid="share-headline-input"
                                />
                                <p className="text-[10px] mt-1 text-muted-foreground">{headline.length}/140</p>
                            </div>
                            <Button
                                onClick={generate}
                                disabled={busy || !headline.trim()}
                                className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90 h-11"
                                data-testid="share-generate-btn"
                            >
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate card"}
                            </Button>
                        </div>
                    ) : (
                        <div className="mt-4 space-y-4">
                            {imgUrl && (
                                <div className="rounded-xl overflow-hidden border border-border">
                                    <img src={imgUrl} alt="Your Owed share card" className="w-full block" data-testid="share-card-preview" />
                                </div>
                            )}
                            <div className="flex items-center gap-2 rounded-full bg-secondary px-4 h-11">
                                <span className="text-xs font-mono text-muted-foreground truncate flex-1" data-testid="share-url">{pageUrl}</span>
                                <button onClick={copyLink} className="text-muted-foreground hover:text-foreground" aria-label="Copy link" data-testid="share-copy-btn">
                                    <Copy className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <Button asChild className="rounded-full bg-[#1da1f2] text-white hover:opacity-90 h-11" data-testid="share-tweet-btn">
                                    <a href={tweetUrl} target="_blank" rel="noreferrer">
                                        <Twitter className="h-4 w-4 mr-1" /> Tweet
                                    </a>
                                </Button>
                                <Button asChild variant="outline" className="rounded-full h-11" data-testid="share-open-public">
                                    <a href={pageUrl} target="_blank" rel="noreferrer">Open public page</a>
                                </Button>
                            </div>
                            <button
                                onClick={revoke}
                                disabled={busy}
                                className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
                                data-testid="share-revoke-btn"
                            >
                                <X className="h-3 w-3" /> Revoke this share link
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

function formatTotal(n) {
    if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
    return `$${Math.round(n).toLocaleString()}`;
}
